import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import api, { formatApiError, imgUrl } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { ImageField } from "../../components/admin/fields";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

const EMPTY_CAT = { name: "", description: "", image: "", image_alt: "", show_image: true, active: true, seo_title: "", seo_description: "" };

export default function AdminCategories() {
  const [categories, setCategories] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CAT);

  const load = () => api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY_CAT }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY_CAT, ...c }); setOpen(true); };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= categories.length) return;
    const a = categories[i], b = categories[j];
    const payload = (c, order) => ({ ...EMPTY_CAT, ...c, order });
    try {
      await api.put(`/admin/categories/${a.id}`, payload(a, j));
      await api.put(`/admin/categories/${b.id}`, payload(b, i));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/categories/${editing.id}`, { ...form, order: editing.order });
      else await api.post("/admin/categories", form);
      toast.success("Category saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Category deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!categories) return <div className="h-40 bg-muted animate-pulse" />;

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl font-semibold">Categories</h1>
        <button onClick={openNew} className="bg-[#111] text-[#FAF9F6] px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-gold hover:text-[#111] transition-colors duration-300 flex items-center gap-2" data-testid="add-category-button">
          <Plus strokeWidth={1.5} size={14} /> Add Category
        </button>
      </div>
      <div className="bg-white border border-black/10 divide-y divide-black/5">
        {categories.map((c) => (
          <div key={c.id} className="flex items-center gap-4 p-4" data-testid={`admin-category-${c.slug}`}>
            <div className="w-12 h-12 border border-black/10 bg-muted shrink-0">
              {c.image && <img src={c.image} alt="" className="w-full h-full object-cover" />}
            </div>
            <p className="flex-1 text-sm font-medium">{c.name}</p>
            <button onClick={() => move(categories.indexOf(c), -1)} disabled={categories.indexOf(c) === 0} className="p-2 border border-black/10 hover:border-gold disabled:opacity-30 transition-colors duration-300" data-testid={`cat-up-${c.slug}`} aria-label="Move up"><ArrowUp strokeWidth={1.5} size={14} /></button>
            <button onClick={() => move(categories.indexOf(c), 1)} disabled={categories.indexOf(c) === categories.length - 1} className="p-2 border border-black/10 hover:border-gold disabled:opacity-30 transition-colors duration-300" data-testid={`cat-down-${c.slug}`} aria-label="Move down"><ArrowDown strokeWidth={1.5} size={14} /></button>
            <button onClick={() => openEdit(c)} className="p-2 hover:text-gold transition-colors duration-300" data-testid={`edit-cat-${c.slug}`} aria-label="Edit"><Pencil strokeWidth={1.5} size={16} /></button>
            <button onClick={() => remove(c)} className="p-2 hover:text-destructive transition-colors duration-300" data-testid={`delete-cat-${c.slug}`} aria-label="Delete"><Trash2 strokeWidth={1.5} size={16} /></button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF9F6]" data-testid="category-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="flex flex-col gap-4 mt-2">
            <Input required placeholder="Category Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white" data-testid="cat-name" />
            <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white" data-testid="cat-description" />
            <ImageField label="Category Image (upload your real product/craft photo)" value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="cat-image" hint="Shown on the homepage category grid. Remove it to show a clean brand placeholder instead." />
            <div>
              <Label className="text-xs uppercase tracking-widest">Image Alt Text (for Google Images)</Label>
              <Input value={form.image_alt} onChange={(e) => setForm({ ...form, image_alt: e.target.value })} className="mt-2 bg-white" placeholder="e.g. Handcrafted metal candle holders" data-testid="cat-image-alt" />
            </div>
            <label className="flex items-center justify-between text-sm" data-testid="cat-active-row">
              Show category on storefront <Switch checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="cat-active" />
            </label>
            <label className="flex items-center justify-between text-sm" data-testid="cat-show-image-row">
              Show category image <Switch checked={form.show_image !== false} onCheckedChange={(v) => setForm({ ...form, show_image: v })} data-testid="cat-show-image" />
            </label>
            <button type="submit" className="bg-[#111] text-[#FAF9F6] py-3 text-xs uppercase tracking-widest hover:bg-gold hover:text-[#111] transition-colors duration-300" data-testid="cat-save">Save</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
